import { createRouteRef, createSubRouteRef, createPlugin, createApiFactory, discoveryApiRef, identityApiRef, errorApiRef, fetchApiRef, createRoutableExtension, createComponentExtension } from '@backstage/core-plugin-api';
import { createSearchResultListItemExtension } from '@backstage/plugin-search-react';
import { DateTime } from 'luxon';
import { WebStorage } from '@backstage/core-app-api';
import { ResponseError } from '@backstage/errors';
import { announcementsApiRef as announcementsApiRef$1 } from '@procore-oss/backstage-plugin-announcements-react';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const lastSeenKey = "user_last_seen_date";
class AnnouncementsClient {
  constructor(opts) {
    __publicField(this, "discoveryApi");
    __publicField(this, "identityApi");
    __publicField(this, "webStorage");
    __publicField(this, "fetchApi");
    this.discoveryApi = opts.discoveryApi;
    this.identityApi = opts.identityApi;
    this.webStorage = new WebStorage("announcements", opts.errorApi);
    this.fetchApi = opts.fetchApi;
  }
  async fetch(input, init) {
    const baseApiUrl = await this.discoveryApi.getBaseUrl("announcements");
    const { token } = await this.identityApi.getCredentials();
    const headers = new Headers(init == null ? void 0 : init.headers);
    if (token && !headers.has("authorization")) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return this.fetchApi.fetch(`${baseApiUrl}${input}`, {
      ...init,
      headers
    }).then(async (response) => {
      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }
      return response.json();
    });
  }
  async delete(input, init) {
    const baseApiUrl = await this.discoveryApi.getBaseUrl("announcements");
    const { token } = await this.identityApi.getCredentials();
    const headers = new Headers(init == null ? void 0 : init.headers);
    if (token && !headers.has("authorization")) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return this.fetchApi.fetch(`${baseApiUrl}${input}`, {
      ...{ method: "DELETE" },
      headers
    }).then(async (response) => {
      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }
    });
  }
  async announcements({
    max,
    page,
    category
  }) {
    const params = new URLSearchParams();
    if (category) {
      params.append("category", category);
    }
    if (max) {
      params.append("max", max.toString());
    }
    if (page) {
      params.append("page", page.toString());
    }
    return this.fetch(`/announcements?${params.toString()}`);
  }
  async announcementByID(id) {
    return this.fetch(`/announcements/${id}`);
  }
  async createAnnouncement(request) {
    return await this.fetch(`/announcements`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    });
  }
  async updateAnnouncement(id, request) {
    return this.fetch(`/announcements/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    });
  }
  async deleteAnnouncementByID(id) {
    return this.delete(`/announcements/${id}`, { method: "DELETE" });
  }
  async categories() {
    return this.fetch("/categories");
  }
  async createCategory(request) {
    await this.fetch(`/categories`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    });
  }
  lastSeenDate() {
    const lastSeen = this.webStorage.get(lastSeenKey);
    if (!lastSeen) {
      return DateTime.fromISO("1990-01-01");
    }
    return DateTime.fromISO(lastSeen);
  }
  markLastSeenDate(date) {
    this.webStorage.set(lastSeenKey, date.toISO());
  }
}

const rootRouteRef = createRouteRef({
  id: "announcements"
});
const announcementCreateRouteRef = createSubRouteRef({
  id: "announcements/create",
  path: "/create",
  parent: rootRouteRef
});
const announcementEditRouteRef = createSubRouteRef({
  id: "announcements/edit",
  path: "/edit/:id",
  parent: rootRouteRef
});
const announcementViewRouteRef = createSubRouteRef({
  id: "announcements/view",
  path: "/view/:id",
  parent: rootRouteRef
});
const categoriesListRouteRef = createSubRouteRef({
  id: "announcements/categories",
  path: "/categories",
  parent: rootRouteRef
});

const announcementsPlugin = createPlugin({
  id: "announcements",
  routes: {
    root: rootRouteRef
  },
  apis: [
    createApiFactory({
      api: announcementsApiRef$1,
      deps: {
        discoveryApi: discoveryApiRef,
        identityApi: identityApiRef,
        errorApi: errorApiRef,
        fetchApi: fetchApiRef
      },
      factory: ({ discoveryApi, identityApi, errorApi, fetchApi }) => {
        return new AnnouncementsClient({
          discoveryApi,
          identityApi,
          errorApi,
          fetchApi
        });
      }
    })
  ]
});
const AnnouncementsPage = announcementsPlugin.provide(
  createRoutableExtension({
    name: "AnnouncementsPage",
    component: () => import('./Router-b946d775.esm.js').then((m) => m.Router),
    mountPoint: rootRouteRef
  })
);
const AnnouncementsTimeline = announcementsPlugin.provide(
  createComponentExtension({
    name: "AnnouncementsTimeline",
    component: {
      lazy: () => import('./index-b87c6bbf.esm.js').then((m) => m.AnnouncementsTimeline)
    }
  })
);
const AnnouncementsCard = announcementsPlugin.provide(
  createComponentExtension({
    name: "AnnouncementsCard",
    component: {
      lazy: () => import('./index-5574412a.esm.js').then((m) => m.AnnouncementsCard)
    }
  })
);
const NewAnnouncementBanner = announcementsPlugin.provide(
  createComponentExtension({
    name: "NewAnnouncementBanner",
    component: {
      lazy: () => import('./index-08d8f2a2.esm.js').then(
        (m) => m.NewAnnouncementBanner
      )
    }
  })
);
const AnnouncementSearchResultListItem = announcementsPlugin.provide(
  createSearchResultListItemExtension({
    name: "AnnouncementSearchResultListItem",
    component: () => import('./index-63041b58.esm.js').then(
      (m) => m.AnnouncementSearchResultListItem
    ),
    predicate: (result) => result.type === "announcements"
  })
);

const announcementsApiRef = announcementsApiRef$1;

export { AnnouncementsPage as A, NewAnnouncementBanner as N, announcementCreateRouteRef as a, announcementViewRouteRef as b, categoriesListRouteRef as c, announcementEditRouteRef as d, announcementsApiRef as e, announcementsPlugin as f, AnnouncementsTimeline as g, AnnouncementsCard as h, AnnouncementSearchResultListItem as i, rootRouteRef as r };
//# sourceMappingURL=index-859dab9f.esm.js.map
